// ---- renderer / scene ----
// The renderer, the camera, the light and the grass: everything a game in this
// house shows before it shows anything of its own. The camera orbits T, which
// follows whoever the game follows — Jiddo round his garden, Tata round her
// sofra — yaw and el are the player's, R follows the viewport, and placeCam()
// puts it where they say.
//
// What is NOT here is the set. His pond, his six trees and his house are his
// (jiddo/src/set.ts), and hers are hers. The line is: if a three-year-old could
// tell which game he is in by looking at it, it belongs to the game.
import * as THREE from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {asset} from './assets';
import {$} from './util';
import {grassGeometry,grassMaterial} from './grass';

// r185 is WebGL2-only, so the one failure worth catching is a device without it.
function makeRenderer(){
  try{return new THREE.WebGLRenderer({canvas:$('c'),antialias:devicePixelRatio<2,powerPreference:'high-performance'});}
  catch(err){$('hint').textContent='هذا الجهاز لا يدعم WebGL2';throw err;}
}
export const renderer=makeRenderer();
renderer.setPixelRatio(Math.min(devicePixelRatio,2));
renderer.shadowMap.enabled=true;
export const scene=new THREE.Scene();
scene.background=new THREE.Color(0xBFE9FF);
const fog=new THREE.Fog(0xBFE9FF,18,40);      // same colour as the sky: the fog IS the horizon
scene.fog=fog;
export const cam=new THREE.PerspectiveCamera(48,1,0.1,60);
// What the camera orbits: the grown-up, at the waist. T eases after them
// (follow), so they are always in the frame and big in it, and the field can be
// wider than the frame — the camera used to hold the whole field from one spot,
// which is a choice between a field with room in it and a Jiddo you can see.
export const T=new THREE.Vector3(0,1.5,0);
// The home pose; portrait widens and backs off a little (fitViewport). yaw and
// el are the player's — input.ts turns them, and the loop eases el home — while
// R is the orbit's radius.
//
// zoom is the pinch (input.ts), and it is a MULTIPLIER on R rather than a new R
// because fitViewport rewrites R on every resize — and on a phone that fires
// whenever the address bar slides, which would have quietly undone the zoom
// mid-round. camR() is where the camera actually is; everything that cares
// about distance asks it rather than reading R.
export const view={yaw:0,el:0.28,R:8.5,zoom:1};
export const ZOOM_IN=0.6, ZOOM_OUT=2;          // close on a face, and the whole field in one shot
export const camR=()=>view.R*view.zoom;
export function placeCam(y:number,e:number){
  const R=camR();
  cam.position.set(T.x+R*Math.sin(y)*Math.cos(e),T.y+R*Math.sin(e),T.z+R*Math.cos(y)*Math.cos(e));
  // The fog IS the horizon, so it rides out with the camera: fixed at the
  // fitted R it would have closed to within arm's reach of a zoomed-out view.
  fog.near=R+10; fog.far=R+34;
  cam.lookAt(T); cam.updateMatrixWorld();
}
// T after whoever the camera follows: a lag of half a unit at walking pace,
// settled a couple of seconds after they stop
export function follow(x:number,z:number,dt:number){const k=1-Math.exp(-1.6*dt);T.x+=(x-T.x)*k;T.z+=(z-T.z)*k;}
// how many pixels a unit is at depth 1: a thing s across and d deep is s/d*pxPerUnit() pixels
export const pxPerUnit=()=>innerHeight/(2*Math.tan(cam.fov*Math.PI/360));
// the camera and the canvas, fitted to a viewport w by h
export function fitViewport(w:number,h:number){
  const a=w/h;
  cam.aspect=a; cam.fov=a<1?68:48; view.R=a<1?10:8.5;
  cam.updateProjectionMatrix(); renderer.setSize(w,h);
}
scene.add(new THREE.HemisphereLight(0xffffff,0x79C158,0.9));
const sun=new THREE.DirectionalLight(0xffffff,1.8);
sun.position.set(6,10,4); sun.castShadow=true;
sun.shadow.mapSize.set(2048,2048); sun.shadow.normalBias=0.04;
Object.assign(sun.shadow.camera,{left:-16,right:16,bottom:-16,top:16,near:1,far:34});
scene.add(sun);

// The two materials and the one helper every set is drawn with, exported so a
// game's own set.ts draws in the same idiom rather than inventing a second one.
export const TOON=(c:number)=>new THREE.MeshToonMaterial({color:c});
export const LAMB=(c:number)=>new THREE.MeshLambertMaterial({color:c});
export function mesh(geo:THREE.BufferGeometry,mat:THREE.Material,x:number,y:number,z:number,parent?:THREE.Object3D){
  const m=new THREE.Mesh(geo,mat);m.position.set(x,y,z);m.castShadow=true;(parent||scene).add(m);return m;
}
// A ring of trees, one per spot, in the game's own green. Where they stand is
// the game's — out past where anyone walks (they have no colliders).
//
// Every tree is the one model, core/tree.glb: a bought 4.5-million-triangle
// tree cut to 13,000, with its texture baked down to a grey shade on each
// vertex and split in two by colour, bark and leaf (scripts/bake-tree.mjs).
// The colour itself is the game's, multiplied over that shade — the leaf green
// passed here, the bark ours — so the canopy keeps its depth and still reads
// as the game's green. The file is 1 unit tall, standing on its trunk at the
// origin, and TREE_H makes it half as tall again as Jiddo (2.4).
//
// The path is from the site's root, not the page, because three pages load
// this — jiddo/, tata/ and every family's f/<slug>/ — at two different depths.
const TREE='/core/tree.glb', TREE_H=3.6, BARK=0x8A5A34;
// Each tree's canopy as a cylinder — x, z and r across, lo to hi up — and the
// tree's own two materials, so it can fade on its own (fadeTrees).
interface Tree{o:THREE.Object3D;x:number;z:number;r:number;lo:number;hi:number;mats:THREE.MeshToonMaterial[];a:number}
const grove:Tree[]=[];
export function trees(at:[number,number][],leaf:number){
  new GLTFLoader().loadAsync(asset(TREE)).then(g=>{
    g.scene.traverse(o=>{
      const m=o as THREE.Mesh; if(!m.isMesh)return;
      m.geometry.computeVertexNormals();
      const name=(m.material as THREE.Material).name;
      m.material=TOON(name==='leaf'?leaf:BARK); m.material.name=name; (m.material as THREE.MeshToonMaterial).vertexColors=true;
      m.castShadow=true; m.receiveShadow=true;
    });
    // Six of one tree, so each is turned and sized a little differently — by
    // where it stands, so the same spot always grows the same tree.
    for(const [x,z] of at){
      const h=Math.abs(Math.sin(x*12.9898+z*78.233)*43758.5453)%1;
      const t=g.scene.clone(); t.position.set(x,0,z); t.rotation.y=h*Math.PI*2; t.scale.setScalar(TREE_H*(0.9+0.2*h));
      scene.add(t); t.updateMatrixWorld(true);
      const mats:THREE.MeshToonMaterial[]=[], crown=new THREE.Box3();
      t.traverse(o=>{
        const m=o as THREE.Mesh; if(!m.isMesh)return;
        m.material=(m.material as THREE.MeshToonMaterial).clone(); mats.push(m.material as THREE.MeshToonMaterial);
        if((m.material as THREE.Material).name==='leaf')crown.setFromObject(m,true);
      });
      const c=crown.getCenter(new THREE.Vector3()), s=crown.getSize(new THREE.Vector3());
      grove.push({o:t,x:c.x,z:c.z,r:Math.max(s.x,s.z)/2,lo:crown.min.y,hi:crown.max.y,mats,a:1});
    }
  }).catch(err=>console.warn(TREE+' did not load',err));
}
// A tree is taller than the camera flies, and the player can swing the camera
// anywhere round the field: into a canopy, or round behind one so it stands
// between the lens and the grown-up the camera is watching. Either way the
// picture would be a wall of leaves. So a tree the lens is in, or nearly in,
// fades right away, and one standing across the line from the lens to T fades
// to a ghost of itself — still a tree, and the grown-up plain through it — and
// each comes back once it is out of the way.
const GHOST=0.25, sight=new THREE.Vector3();
function gap(t:Tree,p:THREE.Vector3){
  return Math.hypot(Math.max(0,Math.hypot(p.x-t.x,p.z-t.z)-t.r),Math.max(0,t.lo-p.y,p.y-t.hi));
}
export function fadeTrees(dt:number){
  for(const t of grove){
    let d=Infinity;
    for(let i=1;i<=8;i++)d=Math.min(d,gap(t,sight.lerpVectors(cam.position,T,i/8)));
    const want=gap(t,cam.position)<0.6?0:d<0.3?GHOST:1;
    t.a+=(want-t.a)*(1-Math.exp(-8*dt));
    const see=t.a<0.99;
    t.o.visible=t.a>0.02;
    for(const m of t.mats){m.opacity=see?t.a:1; if(m.transparent!==see){m.transparent=see;m.needsUpdate=true;}}
  }
}
// The grass. Both games stand on it, so it is the one piece of set that is here
// (painted in grass.ts).
const ground=mesh(grassGeometry(120),grassMaterial(renderer.capabilities.getMaxAnisotropy()),0,0,0);
ground.rotation.x=-Math.PI/2; ground.receiveShadow=true; ground.castShadow=false;
